class TransactionService {
  constructor() {
    // En la Mac se puede apuntar al emulador de functions con
    // REACT_APP_FUNCTIONS_URL en .env.development.local (ver README, "Probar en local").
    this.baseUrl = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-transferisok.cloudfunctions.net';
    this.currentTransactionIds = new Set();
    // id -> pagador_nombre de lo que ya se mostró, para avisar cuando llega un nombre.
    this.nombresConocidos = new Map();
    // Límite de monto de la última respuesta (undefined = todavía no hubo ninguna).
    this.limite = undefined;
    // Alias y titular de la cuenta de MP de la última respuesta (undefined = ninguna
    // todavía).
    this.alias = undefined;
    this.titular = undefined;
    this.mostrarAlias = undefined;
    this.verComo = null;
    // Búsquedas por monto que todavía no llegaron al backend: viajan con la consulta
    // siguiente, que las deja en el log para medir el uso (ver scripts/uso.js).
    this.busquedasPendientes = [];
    // Toques del ojito del alias ('ocultar' / 'mostrar'), con el mismo viaje.
    this.aliasPendientes = [];
  }

  // El ojito del cartel del alias (AliasCuenta). Tampoco se anota viendo como otro.
  registrarAlias(accion) {
    if (this.verComo) return;
    this.aliasPendientes.push(accion);
    if (this.aliasPendientes.length > 20) this.aliasPendientes.shift();
  }

  // Una búsqueda por monto terminada (ver TransactionsList en Home.js). No se anota
  // la que hace el superadmin viendo como otro usuario.
  registrarBusqueda({ digitos, resultados }) {
    if (this.verComo) return;
    this.busquedasPendientes.push({ digitos, resultados });
    if (this.busquedasPendientes.length > 20) this.busquedasPendientes.shift();
  }

  // Vista por usuario del superadmin: a partir de acá se consultan las
  // transferencias de ese email (null = las propias). El backend solo lo acepta
  // si la sesión tiene el claim de superadmin.
  setVerComo(email) {
    if ((email || null) === this.verComo) return;
    this.verComo = email || null;
    this.currentTransactionIds = new Set();
    this.nombresConocidos = new Map();
    this.limite = undefined;
    this.alias = undefined;
    this.titular = undefined;
    this.mostrarAlias = undefined;
  }

  // El access token de Mercado Pago ya no viaja desde el navegador: lo resuelve el
  // backend a partir del usuario autenticado. Los parámetros token/userCollectorId
  // se mantienen en la firma por compatibilidad, pero se ignoran.
  async getTransactions(token, startDate, endDate, userCollectorId, callbacks = {}) {
    try {
      // Obtener el token de autenticación del usuario actual
      const authToken = await this.getAuthToken();
      const verComo = this.verComo;

      // Uso del home para el log: si la pestaña está a la vista, las búsquedas y los
      // toques del ojito del alias pendientes. Viendo como otro usuario no se manda.
      const uso = verComo ? null : { visible: document.visibilityState === 'visible' };
      if (uso && this.busquedasPendientes.length > 0) {
        uso.busquedas = this.busquedasPendientes.slice();
      }
      if (uso && this.aliasPendientes.length > 0) {
        uso.alias = this.aliasPendientes.slice();
      }

      // Llamar a la función del backend en lugar de hacer llamadas directas a MercadoPago
      const response = await fetch(`${this.baseUrl}/getMercadoPagoTransactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}` // Incluir el token de autenticación
        },
        body: JSON.stringify({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          ...(verComo && { verComo }),
          ...(uso && { uso })
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al obtener transacciones');
      }

      // Las búsquedas y los toques que se mandaron ya quedaron en el log.
      if (uso?.busquedas) {
        this.busquedasPendientes.splice(0, uso.busquedas.length);
      }
      if (uso?.alias) {
        this.aliasPendientes.splice(0, uso.alias.length);
      }

      const data = await response.json();

      // Si mientras tanto se cambió de usuario, la respuesta es de la vista vieja.
      if (verComo !== this.verComo) return [];

      const processedResults = data.transactions || [];

      // Monto máximo que ve este usuario (operadores de una cuenta con límite) o null.
      // Si cambió con la pantalla abierta, la lista se rearma con lo que mandó el
      // backend: así desaparecen las que ya no corresponden y vuelven las que estaban
      // ocultas. onLimite(limite, true) avisa que hay que vaciarla antes.
      const limite = data.limite || null;
      if (limite !== this.limite) {
        const rearmar = this.limite !== undefined;
        this.limite = limite;
        if (rearmar) {
          this.currentTransactionIds = new Set();
          this.nombresConocidos = new Map();
        }
        if (callbacks.onLimite) {
          callbacks.onLimite(limite, rearmar);
        }
      }

      // Alias de la cuenta de MP para el cartel del home (lo carga un admin o llega
      // solo por el CVU), su titular, o null; y si el cartel se muestra.
      const alias = data.alias || null;
      const titular = data.titular || null;
      const mostrarAlias = data.mostrar_alias !== false;
      if (alias !== this.alias || titular !== this.titular || mostrarAlias !== this.mostrarAlias) {
        this.alias = alias;
        this.titular = titular;
        this.mostrarAlias = mostrarAlias;
        if (callbacks.onAlias) {
          callbacks.onAlias(alias, titular, mostrarAlias);
        }
      }

      // Detectar nuevas transacciones
      const newTransactions = processedResults.filter(
        transaction => !this.currentTransactionIds.has(transaction.id)
      );

      // Las que ya se mostraron y ahora traen el nombre de quien transfirió: puede
      // llegar en una consulta posterior (el backend no espera a ARCA más de unos
      // segundos). Si una respuesta viene sin nombre, se conserva el que ya estaba.
      const updatedTransactions = processedResults.filter(
        transaction => this.currentTransactionIds.has(transaction.id) &&
          transaction.pagador_nombre &&
          transaction.pagador_nombre !== this.nombresConocidos.get(transaction.id)
      );

      // Actualizar el conjunto de IDs conocidos
      processedResults.forEach(transaction => {
        this.currentTransactionIds.add(transaction.id);
        if (transaction.pagador_nombre) {
          this.nombresConocidos.set(transaction.id, transaction.pagador_nombre);
        }
      });

      // Llamar al callback con las nuevas transacciones si existen
      if (newTransactions.length > 0 && callbacks.onNewTransactions) {
        callbacks.onNewTransactions(newTransactions);
      }

      if (updatedTransactions.length > 0 && callbacks.onUpdatedTransactions) {
        callbacks.onUpdatedTransactions(updatedTransactions);
      }

      if (callbacks.onSetTransactionIds) {
        callbacks.onSetTransactionIds(new Set(processedResults.map(t => t.id)));
      }

      if (callbacks.onSetManualReload) {
        callbacks.onSetManualReload(false);
      }

      if (callbacks.onSetResults) {
        callbacks.onSetResults(processedResults);
      }

      return processedResults;
    } catch (error) {
      console.error('Error en getTransactions:', error);
      throw error;
    }
  }

  async findTransactionByAmount(transactions, amount) {
    // Esta función puede seguir siendo local ya que solo opera sobre datos ya obtenidos
    return transactions.find(t => t.transaction_amount === parseFloat(amount));
  }

  // Método alternativo que usa el backend para buscar por monto
  async findTransactionByAmountFromBackend(token, startDate, endDate, userCollectorId, amount) {
    try {
      // Obtener el token de autenticación del usuario actual
      const authToken = await this.getAuthToken();

      const response = await fetch(`${this.baseUrl}/findMercadoPagoTransactionByAmount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}` // Incluir el token de autenticación
        },
        body: JSON.stringify({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          amount
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al buscar transacción por monto');
      }

      const data = await response.json();
      return data.transaction;
    } catch (error) {
      console.error('Error en findTransactionByAmountFromBackend:', error);
      throw error;
    }
  }

  // Método para obtener el token de autenticación del usuario actual
  async getAuthToken() {
    try {
      // Importar Firebase de manera dinámica para evitar problemas de inicialización
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error('No hay usuario autenticado');
      }

      return await currentUser.getIdToken();
    } catch (error) {
      console.error('Error al obtener token de autenticación:', error);
      throw error;
    }
  }
}

export const transactionService = new TransactionService();